import { FC } from 'react';

import { GrammarCheckAlert } from '../types';

export interface HighlighterProps {
  checkableElement: CheckableElement;
  targetEl: HTMLElement;
  alerts: GrammarCheckAlert[];
  onInsert?: (alert: GrammarCheckAlert, replacement: string) => void;
  onDismiss?: (alert: GrammarCheckAlert) => void;
  onPopoverShown?: (alert: GrammarCheckAlert) => void;
}

export interface CheckableElement {
  element: HTMLElement;
  calculateAlertClientRects(alerts: GrammarCheckAlert[]): DOMRect[][];
  insertText(text: string): void;
  replaceText(alert: GrammarCheckAlert): Promise<void>;
  replaceAllText?(alerts: GrammarCheckAlert[]): Promise<void>;
  getText(): string;

  getHighlighterContainer(): HTMLElement | null;
  getIconContainer(): HTMLElement | null;
  getIconPlacement(): string;
  getIconOffset(): [number, number];
  isIconCollapsed(): boolean;

  destroy(): void;
  adjustAlerts(alerts: GrammarCheckAlert[]): GrammarCheckAlert[];
  getHighlighter(): FC<HighlighterProps>;
}

export abstract class BaseCheckableElement implements CheckableElement {
  element: HTMLElement;

  constructor(element: HTMLElement) {
    this.element = element;
  }

  abstract calculateAlertClientRects(alerts: GrammarCheckAlert[]): DOMRect[][];
  abstract insertText(text: string): void;
  abstract replaceText(alert: GrammarCheckAlert): Promise<void>;
  abstract getText(): string;
  abstract getHighlighter(): FC<HighlighterProps>;

  getHighlighterContainer(): HTMLElement | null {
    return null;
  }

  getIconContainer(): HTMLElement | null {
    return this.element;
  }

  getIconPlacement(): string {
    return 'bottom-end';
  }

  getIconOffset(): [number, number] {
    const padRight = this.element.offsetWidth - this.element.clientWidth;
    return this.isIconCollapsed() ? [-padRight, -20] : [-padRight, -35];
  }

  isIconCollapsed(): boolean {
    return this.element.getBoundingClientRect().height < 24;
  }

  destroy(): void {}

  adjustAlerts = (alerts: GrammarCheckAlert[]): GrammarCheckAlert[] => {
    return alerts.reduce(
      (acc, alert) => {
        const { absOffset, length, replacements } = alert;
        const bestReplacement = replacements[0] || '';
        const prevOffsetShift = acc.offsetShift;
        const adjustedAbsOffset = absOffset + prevOffsetShift;
        // @ts-ignore
        acc.adjustedAlerts.push({
          ...alert,
          absOffset: adjustedAbsOffset,
        });

        const lengthDifference = bestReplacement.length - length;
        acc.offsetShift += lengthDifference;

        return acc;
      },
      { adjustedAlerts: [] as GrammarCheckAlert[], offsetShift: 0 },
    ).adjustedAlerts;
  };
}
